<template>
  <div
    id="select"
    :style="props.disabled ? 'opacity: 0.6;' : ''"
    :class="props.router ? 'custom-router' : 'custom-select'"
    @keydown="handleKeydown"
  >
    <label
      v-if="props.placeholder && props.floatPlaceholder"
      :class="['floating-label', { active: isLabelActive }]"
    >
      {{ props.placeholder }}
    </label>
    <input
      v-model="searchQuery"
      @input="onInput"
      @focus="onFocus"
      :disabled="props.disabled"
      @blur="onBlur"
      :placeholder="
        props.placeholder && props.floatPlaceholder ? '' : props.placeholder
      "
      ref="inputRef"
      :style="`${props.readonly ? 'cursor: default;' : 'cursor: text;'} ${
        props.floatPlaceholder
          ? 'height: 56px; padding: 24px 32px 8px 16px; font-size: 16px'
          : ''
      } ${props.error ? 'border-color: var(--error)' : ''} ${
        searchQuery && !props.loader && props.deleteBtn
          ? 'padding-right: 40px'
          : ''
      }`"
      :readonly="props.readonly"
    />
    <MenuDownIcon
      v-if="!props.floatPlaceholder"
      @mousedown.prevent="focusInput()"
      :style="props.floatPlaceholder ? 'top: 20px' : ''"
      class="menu"
    />
    <div @click="clear">
      <XIcon
        v-if="searchQuery && !props.loader && props.deleteBtn"
        :class="props.router ? 'clear_router' : 'clear'"
      />
    </div>
    <SpinnerSvg
      v-if="props.loader && isFocused"
      :class="props.router ? 'clear_router' : 'clear'"
    />
    <ul v-if="isOpen" class="options-list" ref="optionsList">
      <template v-if="filteredOptions.length">
        <li
          v-for="(option, index) in filteredOptions"
          :key="index"
          @mousedown.stop="selectOption(option)"
          :class="{
            'option-item': true,
            'is-active': index === highlightedIndex,
          }"
          :ref="setOptionRef"
          @mouseenter.stop="highlightedIndex = index"
        >
          <p>
            <slot name="option" :option="option">
              {{ getOptionLabel(option) }}
            </slot>
          </p>
          <div v-if="getOptionValue(option) === props.value" class="checked">
            <CheckMarkIcon />
          </div>
        </li>
      </template>
      <li v-else class="option-item_empty">Данные отсутствуют!</li>
    </ul>
  </div>
</template>

<script>
import { MenuDownIcon, CheckMarkIcon, XIcon, SpinnerSvg } from "./icon/index";

export default {
  name: "CustomSelect",
  components: {
    MenuDownIcon,
    CheckMarkIcon,
    XIcon,
    SpinnerSvg,
  },
  props: {
    deleteBtn: {
      type: Boolean,
      default: true,
    },
    value: {
      type: [String, Number, Object],
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    valueKey: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Поиск",
    },
    floatPlaceholder: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    loader: {
      type: Boolean,
      default: false,
    },
    forceUpdateSearchQuery: {
      type: Boolean,
      default: true,
    },
    router: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
      isOpen: false,
      inputRef: null,
      highlightedIndex: -1,
      currentValue: null,
      isFocused: false,
      optionRefs: [],
    };
  },
  computed: {
    isLabelActive() {
      return this.isFocused || !!this.searchQuery;
    },
    filteredOptions() {
      if (!Array.isArray(this.options)) return [];

      if (!this.search || !this.searchQuery || this.readonly)
        return this.options;

      return this.options.filter((option) => {
        const label = this.getOptionLabel(option).toLowerCase();
        return label.includes(this.searchQuery.toLowerCase());
      });
    },
    props() {
      return this.$props;
    },
  },
  watch: {
    value: {
      handler() {
        this.updateCurrentValue();
      },
      immediate: true,
    },
    options: {
      handler() {
        this.updateCurrentValue();
      },
      immediate: true,
    },
  },
  mounted() {
    this.inputRef = this.$refs.inputRef;
    this.updateCurrentValue();
  },
  methods: {
    getOptionLabel(option) {
      if (typeof option === "object" && this.label) {
        return option[this.label];
      }
      return String(option);
    },
    getOptionValue(option) {
      if (typeof option === "object" && this.valueKey) {
        return option[this.valueKey];
      }
      return option;
    },
    setOptionRef(el) {
      if (el) this.optionRefs.push(el);
    },
    focusInput() {
      this.inputRef && this.inputRef.focus();
    },
    onInput() {
      this.isOpen = true;
      this.highlightedIndex = -1;
      this.$emit("onInput", this.searchQuery);
    },
    clear() {
      this.searchQuery = "";
      this.$emit("input", null);
      this.$emit("onClear");
      this.currentValue = null;
    },
    selectOption(option) {
      const value = this.valueKey ? this.getOptionValue(option) : option;
      this.$emit("input", value);
      this.$emit("onSelect", option);
      this.searchQuery = this.getOptionLabel(option);
      this.isOpen = false;
      this.highlightedIndex = -1;
    },
    onFocus() {
      this.isOpen = true;
      this.isFocused = true;
    },
    onBlur() {
      this.isFocused = false;
      const currentLabel = this.currentValue
        ? this.getOptionLabel(this.currentValue)
        : "";
      if (!this.searchQuery.trim()) {
        this.clear();
        this.isOpen = false;
        return;
      }
      if (this.searchQuery !== currentLabel) {
        this.searchQuery = currentLabel;
      }
      this.isOpen = false;
    },
    handleKeydown(event) {
      if (!this.isOpen || this.filteredOptions.length === 0) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.highlightedIndex =
          (this.highlightedIndex + 1) % this.filteredOptions.length;
        this.scrollToHighlighted();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        this.highlightedIndex =
          (this.highlightedIndex - 1 + this.filteredOptions.length) %
          this.filteredOptions.length;
        this.scrollToHighlighted();
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (this.highlightedIndex >= 0) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const el = this.optionRefs[this.highlightedIndex];
        if (el && this.$refs.optionsList) {
          const container = this.$refs.optionsList;
          const elTop = el.offsetTop;
          const elBottom = elTop + el.offsetHeight;
          const containerTop = container.scrollTop;
          const containerBottom = containerTop + container.offsetHeight;

          if (elTop < containerTop) {
            container.scrollTop = elTop;
          } else if (elBottom > containerBottom) {
            container.scrollTop = elBottom - container.offsetHeight;
          }
        }
      });
    },
    updateCurrentValue() {
      if (
        this.value !== null &&
        this.value !== undefined &&
        this.value !== "" &&
        Array.isArray(this.options) &&
        this.options.length
      ) {
        const option = this.options.find((o) => {
          return (
            (this.valueKey && this.getOptionValue(o) === this.value) ||
            (!this.valueKey && o === this.value)
          );
        });
        if (!this.isFocused && !this.searchQuery) {
          this.searchQuery = option ? this.getOptionLabel(option) : "";
        }
        this.currentValue = option || null;
      } else if (!this.isFocused) {
        this.searchQuery = "";
        this.currentValue = null;
      }
    },
    updateText() {
      const option = this.options.find((o) => {
        return (
          (this.valueKey && this.getOptionValue(o) === this.value) ||
          (!this.valueKey && o === this.value)
        );
      });
      this.searchQuery = option ? this.getOptionLabel(option) : "";
    },
  },
};
</script>

<style scoped>
.custom-select {
  position: relative;
  min-width: 100px;
  width: 100%;
  &:focus-within .menu {
    transform: rotate(180deg);
  }
}

.custom-router {
  position: relative;
  min-width: 100px;
  width: 100%;
  &:focus-within .menu {
    transform: rotate(180deg);
  }
}

.custom-router input {
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: #f3f4f6; /* светло-серый фон */
  padding: 5px 12px;
  padding-right: 44px;
  font-size: 14px;
  line-height: 20px;
  border: 1px solid transparent;
  transition-duration: 200ms;
  color: #111827; /* почти чёрный */

  &:focus {
    background-color: #ffffff;
    border-color: #3b82f6; /* синий */
    outline: none;
  }
}

/* INPUT */
.custom-select input {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: #f3f4f6;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 20px;
  border: 1px solid transparent;
  transition-duration: 200ms;
  color: #111827;

  &:focus {
    background-color: #ffffff;
    border-color: #3b82f6;
    outline: none;
  }
}

/* ИКОНКИ */
.menu {
  position: absolute;
  top: 9px;
  right: 8px;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
  color: #6b7280;
}

.clear {
  position: absolute;
  top: 8px;
  right: 24px;
  cursor: pointer;
  color: #9ca3af;
  pointer-events: auto;
}

.clear_router {
  position: absolute;
  top: 13px;
  right: 15px;
  cursor: pointer;
  color: #9ca3af;
  pointer-events: auto;
}

.loader {
  position: absolute;
  top: 7px;
  right: 24px;
  cursor: pointer;
}

.options-list {
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  z-index: 9999;
  background-color: #ffffff;
  overflow-y: auto;
  scrollbar-width: thin;
  border-radius: 12px;
  max-height: 200px;
  box-shadow: 0px 2px 16px 0px rgba(0, 0, 0, 0.12);
}
.option-item {
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 12px;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: #1f2937;

  p {
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
  }

  &_empty {
    cursor: default;
    text-align: center;
    padding: 6px;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    color: #9ca3af;
  }
}

.option-item:hover,
.option-item.is-active {
  background-color: #e5e7eb;
  transition: transform 0.3s ease;
}

.checked {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #3b82f6;
  border-radius: 100%;
  border: none;
  max-width: 15px;
  width: 100%;
  height: 15px;
}

.floating-label {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #6b7280;
  pointer-events: none;
  transition: all 0.2s ease;
}

.floating-label.active {
  top: 16px;
  font-size: 12px;
  color: #3b82f6;
}

.option-item_empty {
  cursor: default;
  text-align: center;
  padding: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  color: #9ca3af;
}
</style>
